<script setup>
import {ref, watch, onMounted, computed} from 'vue';
import axios from 'axios';
import {getTemplateContent, editableContent, isAdminView} from '@/composables/email-templates/useEmailTemplates';
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {errorToast, successToast} from "@/composables/useToasts";
import {useMutation} from "@tanstack/vue-query";

const { data: templateContentData } = getTemplateContent();

const previewBody = ref('');
const previewSubject = ref('');
const from = computed(() => {
  if (!templateContentData.value) {
    return {
      from_name: '',
      from_email: ''
    };
  }
  return {
    from_name: templateContentData.value.whitelabel_branding?.custom_email?.from_name || '',
    from_email: editableContent.value.from_email || templateContentData.value.whitelabel_branding?.custom_email?.from_email || ''
  };
});

const extractVariableName = (variable) => {
  return variable.replace('[[', '').replace(']]', '');
};

const getVariableReplacement = (variableName) => {
  const dummyVariable = templateContentData.value.dummy_variables[variableName];
  const resolvedVariableName = dummyVariable || variableName;

  const defaultVariable = templateContentData.value.default_variables[resolvedVariableName];
  return defaultVariable ?? resolvedVariableName ?? 'example data';
};

const replaceEmailComponents = (content) => {
  if (!templateContentData.value.email_content.email_template.components) {
    return content;
  }

  let updatedContent = content;

  templateContentData.value.email_content.email_template.components.forEach(component => {
    const componentVariable = `[[${component.variable_name}]]`;
    updatedContent = updatedContent.replaceAll(componentVariable, component.html);
  });

  return updatedContent;
};

const replaceStandardVariables = (content) => {
  const variablePattern = /\[\[[A-Z_]+\]\]/g;
  const foundVariables = content.match(variablePattern);

  if (!foundVariables || foundVariables.length === 0) {
    return content;
  }

  let updatedContent = content;

  foundVariables.forEach(variable => {
    const variableName = extractVariableName(variable);
    const replacement = getVariableReplacement(variableName);
    updatedContent = updatedContent.replace(variable, replacement);
  });

  return updatedContent;
};

const updatePreview = (content, isSubject) => {
  if (isSubject) {
    previewSubject.value = content;
    return;
  }

  previewBody.value = content;
};

const applyVariables = (rawContent = '', isSubject = false) => {
  const content = rawContent || '';

  if (content.length === 0) {
    updatePreview(content, isSubject);
    return;
  }

  let processedContent = content;

  // Swap components first to prevent name conflicts in general variable search
  if (!isSubject) {
    processedContent = replaceEmailComponents(processedContent);
  }

  processedContent = replaceStandardVariables(processedContent);
  updatePreview(processedContent, isSubject);
};

const {mutate: sendTestEmail} = useMutation({
  mutationFn: async () => {
    return await axios.post('/ajax/v2/mail/test', {
      subject: editableContent.value.subject,
      body: editableContent.value.body,
      template_id: templateContentData.value.email_content.email_template.id,
      whitelabel_example: isAdminView.value,
    })
  },
  onSuccess: ({ data }) => {
    successToast(data.message);
  },
  onError: () => {
    errorToast('Failed to send test email');
  }
});

// Watch for editable content changes
watch(
  () => editableContent.value.body,
  (newValue) => {
    applyVariables(newValue);
  }
);

watch(
  () => editableContent.value.subject,
  (newValue) => {
    applyVariables(newValue, true);
  }
);

// Initialize preview on mount
onMounted(() => {
  if (editableContent.value.body) {
    applyVariables(editableContent.value.body);
  }
  if (editableContent.value.subject) {
    applyVariables(editableContent.value.subject, true);
  }
});

// Also watch for editable content becoming available
watch(
  () => editableContent,
  (newData) => {
    if (newData && newData.body) {
      applyVariables(newData.body);
    }
    if (newData && newData.subject) {
      applyVariables(newData.subject, true);
    }
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8 tw-w-full">
    <div class="tw-flex tw-justify-between tw-items-center">
      <h3 class="tw-text-neutral-900 tw-font-bold tw-text-2xl tw-leading-none">
        Email Preview
      </h3>
      <DefaultButton
        size="small"
        @click="sendTestEmail"
      >
        Send test email
      </DefaultButton>
    </div>

    <div
      v-if="templateContentData"
      class="tw-flex tw-flex-col tw-gap-y-4"
    >
      <div class="tw-flex tw-flex-col tw-gap-4">
        <div class="tw-flex tw-justify-between tw-items-start">
          <h3 class="tw-text-lg tw-font-semibold tw-text-neutral-900 tw-max-w-2/3">
            {{ previewSubject }}
          </h3>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-1 tw-text-sm">
          <div>
            <span class="tw-font-semibold tw-text-neutral-500">From:</span>
            {{ from.from_name }} &lt;{{ from.from_email }}&gt;
          </div>
          <div>
            <span class="tw-font-semibold tw-text-neutral-500">To:</span>
            {{ templateContentData.user.name }} &lt;{{ templateContentData.user.email }}&gt;
          </div>
        </div>
      </div>

      <hr class="tw-border-neutral-300">

      <div class="tw-bg-gray-100 tw-px-12 tw-pt-4 tw-pb-12">
        <div
          v-if="templateContentData.whitelabel_branding.logo"
          class="tw-flex tw-justify-center"
        >
          <img
            :src="templateContentData.whitelabel_branding.logo"
            alt="Logo"
            class="tw-w-[120px] tw-py-5 tw-h-auto"
          >
        </div>

        <div class="tw-bg-white tw-p-12 tw-border tw-border-neutral-300 tw-rounded">
          <div
            class="[&]:tw-font-[Verdana,Geneva,sans-serif] [&_*]:tw-font-[Verdana,Geneva,sans-serif] [&_p]:tw-mb-4"
            v-html="previewBody"
          />
        </div>

        <div
          class="tw-text-center tw-text-sm tw-leading-normal tw-pt-4 tw-text-black"
          style="font-family:Verdana,Geneva,sans-serif"
        >
          Contact
          <a
            :href="`mailto:${from.from_email}`"
            class="tw-text-[#FB7E7A]"
          >
            {{ from.from_email }}
          </a> if you have any questions about this email.
          You can manage your notifications within your account settings.
        </div>
      </div>
    </div>
  </div>
</template>
